import { ITemplate } from "@/server/modules/templates/template.model"
import { apiFetch } from "@/lib/api/apiFetch"

// Simplified template interface for frontend use
export interface Template {
  _id: string
  name: string
  subject: string
  content: string
  type: "HTML" | "Text" | "MJML"
  category: string
  tags: string[]
  variables: string[]
  previewText: string
  isActive: boolean
  isArchived: boolean
  version: number
  parentTemplate?: string
  thumbnailUrl?: string
  usageCount: number
  lastUsed?: Date
  createdBy: string
  createdAt: Date
  updatedAt: Date
}

export interface TemplateListResponse {
  templates: Template[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface TemplateFilters {
  page?: number
  limit?: number
  category?: string
  search?: string
}

export class TemplateAPI {
  static async getTemplates(filters: TemplateFilters = {}): Promise<TemplateListResponse> {
    const params = new URLSearchParams()
    
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.category) params.append('category', filters.category)
    if (filters.search) params.append('search', filters.search)
    
    return apiFetch(`/api/templates?${params.toString()}`)
  }

  static async getTemplate(id: string): Promise<Template> {
    return apiFetch(`/api/templates/${id}`)
  }

  static async getTemplateById(id: string): Promise<Template> {
    return apiFetch(`/api/templates/${id}`) as Promise<Template>
  }

  static async createTemplate(templateData: Partial<Template>): Promise<Template> {
    try {
      return apiFetch('/api/templates', {
        method: 'POST',
        data: {
          ...templateData,
          createdBy: 'system',
        },
      })
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to create template')
    }
  }

  static async updateTemplate(id: string, updates: Partial<Template>): Promise<Template> {
    return apiFetch(`/api/templates/${id}`, {
      method: 'PUT',
      data: updates,
    })
  }

  static async duplicateTemplate(id: string): Promise<Template> {
    const res = await apiFetch(`/api/templates/${id}/duplicate`, { method: 'POST' })
    return res.data
  }

  static async archiveTemplate(id: string, isArchived: boolean): Promise<void> {
    await apiFetch(`/api/templates/${id}`, {
      method: 'PUT',
      data: { isArchived },
    })
  }

  static async deleteTemplate(id: string): Promise<void> {
    await apiFetch(`/api/templates/${id}`, { method: 'DELETE' })
  }

  // Utility method to extract variables from template content
  static extractVariables(content: string): string[] {
    if (!content) return []
    const variableRegex = /\{\{([^}]+)\}\}/g
    const matches = content.match(variableRegex)
    return matches 
      ? [...new Set(matches.map(match => match.replace(/[{}]/g, "").trim()))]
      : []
  }

  // Utility method to validate template data
  static validateTemplate(data: Partial<Template>): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    
    if (!data.name?.trim()) {
      errors.push('Template name is required')
    }
    
    // if (!data.subject?.trim()) {
    //   errors.push('Email subject is required')
    // }
    
    if (!data.content?.trim()) {
      errors.push('Template content is required')
    }
    
    if (data.name && data.name.length > 100) {
      errors.push('Template name cannot exceed 100 characters')
    }
    
    // if (data.subject && data.subject.length > 200) {
    //   errors.push('Subject cannot exceed 200 characters')
    // }
    
    // if (data.previewText && data.previewText.length > 150) {
    //   errors.push('Preview text cannot exceed 150 characters')
    // }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Utility method to generate preview content with sample data
  static generatePreview(content: string, variables?: string[]): string {
    let previewContent = content
    
    const sampleData: Record<string, any> = {
      name: 'John Doe',
      email: 'john@example.com',
      profileUrl: '#',
      unsubscribeUrl: '#',
      preferencesUrl: '#',
      company: 'iComm',
      date: new Date().toLocaleDateString(),
      firstName: 'John',
      lastName: 'Doe',
      username: 'johndoe',
      activationUrl: '#',
      resetUrl: '#',
      loginUrl: '#',
      supportEmail: 'support@icomm.com',
      phone: '+1 (555) 123-4567',
      address: '123 Business St, City, State 12345',
      website: 'https://icomm.com',
      facebook: 'https://facebook.com/icomm',
      twitter: 'https://twitter.com/icomm',
      linkedin: 'https://linkedin.com/company/icomm'
    }
    
    variables?.forEach(variable => {
      const sampleValue = sampleData[variable.toLowerCase()] || sampleData[variable] || `[${variable}]`
      previewContent = previewContent.replace(new RegExp(`\\{\\{${variable}\\}\\}`, 'g'), sampleValue)
    })
    
    return previewContent
  }
}
